import {
	Elements,
	Connection,
	Edge,
	ArrowHeadType,
	Node,
	isNode,
	isEdge,
	FlowElement,
} from 'react-flow-renderer';
import { NODE_TYPES, ReactFlowSchema, RehabAction, Rules } from '@Types';
import { RECORD_TYPE } from '@Utils/choiceListMapping';
import { getLabelForDuplicate } from '@Utils/duplicateNodeName';
import {
	isNonTreeNodeToTreeNode,
	cloneNodes,
	buildNodeParentsMap,
	copySubtree,
} from '@Utils/TreeUtils';

// -------------------------------------------------------------------
// 1. resolveNodeForEdit
// -------------------------------------------------------------------

export type ResolvedNode = {
	type: 'query' | 'action';
	actionId?: string;
};

const getNodeActionId = (element: FlowElement): string =>
	isNode(element) ? (element.data?.nodeData?.actionId ?? '') : '';

const getSchemaActionId = (schema: ReactFlowSchema): string =>
	schema.data?.nodeData?.actionId ?? '';

const findInCurrentTreeNodes = (
	nodeId: string,
	currentTreeNodes: ReactFlowSchema[],
	elements: Elements | undefined,
): ResolvedNode | null => {
	const node = currentTreeNodes.find(element => element.id == nodeId);
	if (!node) return null;

	if (node.type == NODE_TYPES.query) return { type: 'query' };

	// It's an action — prefer elements for actionId, fall back to currentTree
	const elementsNode = elements?.find(element => element.id == nodeId);
	if (elementsNode) {
		return { type: 'action', actionId: getNodeActionId(elementsNode) };
	}
	return { type: 'action', actionId: getSchemaActionId(node) };
};

const findInElements = (
	nodeId: string,
	elements: Elements,
): ResolvedNode | null => {
	const node = elements.find(element => element.id == nodeId);
	if (!node) return null;
	// Original behavior: when found in elements (but not currentTree), always
	// opens the action dialog regardless of node type.
	return { type: 'action', actionId: getNodeActionId(node) };
};

const findInReactFlowElements = (
	nodeId: string,
	reactFlowElements: Elements,
): ResolvedNode | null => {
	const node = reactFlowElements.find(element => element.id == nodeId);
	if (!node) return null;

	if (isNode(node) && node.type == NODE_TYPES.query) {
		return { type: 'query' };
	}
	return { type: 'action', actionId: getNodeActionId(node) };
};

/**
 * Searches for a node across multiple sources in priority order
 * (currentTree.nodes -> elements -> reactFlowElements) and returns
 * its type information, or null if not found anywhere.
 */
export const resolveNodeForEdit = (
	nodeId: string,
	currentTreeNodes: ReactFlowSchema[] | undefined,
	elements: Elements | undefined,
	reactFlowElements: Elements | undefined,
): ResolvedNode | null => {
	if (currentTreeNodes) {
		const result = findInCurrentTreeNodes(
			nodeId,
			currentTreeNodes,
			elements,
		);
		if (result) return result;
	}
	if (elements) {
		const result = findInElements(nodeId, elements);
		if (result) return result;
	}
	if (reactFlowElements) {
		return findInReactFlowElements(nodeId, reactFlowElements);
	}
	return null;
};

// -------------------------------------------------------------------
// 2. duplicateNode
// -------------------------------------------------------------------

/**
 * Creates a duplicate of the found node. For query nodes: rename label
 * via getLabelForDuplicate. For action nodes: keep label. Both: new ID,
 * position {x:24,y:24}, reset assetsCount to {}.
 * Returns the duplicate Node or null if nodeToDuplicate is not found.
 */
export const duplicateNode = (
	nodeId: string,
	elements: Elements,
): Node | null => {
	const found = elements.find(e => e.id === nodeId);
	if (!found || !isNode(found)) return null;
	const nodeToDuplicate = found;

	if (nodeToDuplicate.type == NODE_TYPES.query) {
		const newId = 'idx_' + Date.now().toString();
		const newLabel = getLabelForDuplicate(
			nodeToDuplicate.data.label,
			elements,
		);
		return {
			id: newId,
			type: NODE_TYPES.query,
			position: {
				x: 24,
				y: 24,
			},
			data: {
				...nodeToDuplicate.data,
				id: newId,
				label: newLabel,
				title: newLabel,
				nodeData: {
					...nodeToDuplicate.data.nodeData,
					title: newLabel,
					assetsCount: {},
				},
			},
		};
	} else if (nodeToDuplicate.type == NODE_TYPES.action) {
		const newId = 'idx_' + Date.now().toString();
		return {
			id: newId,
			type: NODE_TYPES.action,
			position: {
				x: 24,
				y: 24,
			},
			data: {
				...nodeToDuplicate.data,
				id: newId,
				nodeData: {
					...nodeToDuplicate.data.nodeData,
					assetsCount: {},
				},
			},
		};
	}

	return null;
};

// -------------------------------------------------------------------
// 3. validateConnection
// -------------------------------------------------------------------

export type ConnectionValidation = {
	blocked: boolean;
	reason?: string;
};

/**
 * Validates whether a connection is allowed.
 * Returns { blocked: boolean, reason?: string }.
 *
 * isLoop is passed in as a function to avoid duplicating the recursive
 * loop-detection logic here (it depends on component-level state in the original).
 */
export const validateConnection = (
	params: Connection | Edge,
	elements: Elements,
	currentTreeStartNodeId: string,
	shouldEnableMultipleInputs: string | Object,
	isLoop: (
		sourceId: string | null,
		targetId: string | null,
		allNodes: Node[],
		allEdges: Edge[],
	) => boolean,
): ConnectionValidation => {
	// Self-connection
	if (params.source == params.target) {
		return { blocked: true, reason: 'self-connection' };
	}

	// Action source — can't connect from an action node
	for (const ele of elements) {
		if (
			isNode(ele) &&
			ele.id == params.source &&
			ele.type == NODE_TYPES.action
		) {
			return { blocked: true, reason: 'action-source' };
		}
	}

	// Start node limit — only 1 edge from start
	const existingStartNodeEdges = elements.filter(
		elem =>
			isEdge(elem) &&
			elem.source === params.source &&
			params.source === currentTreeStartNodeId,
	);
	if (existingStartNodeEdges.length === 1) {
		return { blocked: true, reason: 'start-node-limit' };
	}

	// Existing target (when !multiInputs)
	const allEdges = elements.filter(isEdge);
	if (!shouldEnableMultipleInputs) {
		const connectingToSameNode = allEdges.find(
			edge => edge.target == params.target,
		);
		if (connectingToSameNode != null) {
			return { blocked: true, reason: 'existing-target' };
		}
	}

	// Loop detection
	const allNodes = elements.filter(isNode);
	if (isLoop(params.source, params.target, allNodes, allEdges)) {
		return { blocked: true, reason: 'loop' };
	}

	// Non-tree to tree node
	if (
		shouldEnableMultipleInputs &&
		isNonTreeNodeToTreeNode(
			params.source,
			params.target,
			elements,
			currentTreeStartNodeId,
		)
	) {
		return { blocked: true, reason: 'nonTreeToTree' };
	}

	// Max edges (>1 from source means already 2, so block)
	const existingEdges = elements.filter(
		elem => isEdge(elem) && elem.source === params.source,
	);
	if (existingEdges.length > 1) {
		return { blocked: true, reason: 'max-edges' };
	}

	return { blocked: false };
};

// -------------------------------------------------------------------
// 4. createConnectionEdge
// -------------------------------------------------------------------

/**
 * Creates the edge with correct True/False label based on existing
 * edges from source. Returns Edge | null.
 */
export const createConnectionEdge = (
	params: Connection | Edge,
	elements: Elements,
): Edge | null => {
	const existingEdges = elements.filter(
		elem => isEdge(elem) && elem.source === params.source,
	) as Edge[];

	if (existingEdges.length > 1) {
		// More than 1 existing edge from source — shouldn't create another
		return null;
	}

	let newLabel = '';
	if (existingEdges.length === 1) {
		// This does a text comparison so have to compare to the translation
		const isTrue = ((existingEdges[0].label as string) ?? '').includes(
			'True',
		);
		newLabel = isTrue ? 'False' : 'True';
	}

	return {
		id: params.source + '_' + params.target,
		source: params.source || '',
		target: params.target || '',
		arrowHeadType: ArrowHeadType.ArrowClosed,
		label: newLabel,
		type: 'buttonedge',
	};
};

// -------------------------------------------------------------------
// 5. cloneMultiParentNodes
// -------------------------------------------------------------------

/**
 * Orchestrates the multi-parent cloning using existing TreeUtils functions.
 * Returns { nodes, edges }.
 */
export const cloneMultiParentNodes = (
	currentNodes: Node[],
	currentEdges: Edge[],
): { nodes: Node[]; edges: Edge[] } => {
	const nodeParentsMap = buildNodeParentsMap(currentEdges);

	let clonedEdges: Edge[] = [];
	const clonedNodes = cloneNodes(
		currentNodes,
		currentEdges,
		clonedEdges,
		nodeParentsMap,
	);

	// remove the edges that have been cloned from current edges
	let filteredEdges = currentEdges.filter(
		edge =>
			!clonedEdges.some(
				clonedEdge =>
					edge.target === clonedEdge.target.split('_clone_')[0] &&
					edge.source === clonedEdge.source,
			),
	);

	// add the cloned edges to the current edges
	filteredEdges = [...filteredEdges, ...clonedEdges];
	let allNodes = [...currentNodes, ...clonedNodes];

	clonedEdges = [];
	const nodesClonedInSubtree: Node[] = [];

	// if there's subtree for the cloned nodes, clone the subtree
	if (clonedNodes.length > 0) {
		clonedNodes.forEach(clonedNode => {
			copySubtree(
				clonedNode.id.split('_clone_')[0],
				clonedNode.id,
				filteredEdges,
				clonedEdges,
				allNodes,
				nodesClonedInSubtree,
			);
		});
	}

	// merge the newly cloned nodes with the current nodes
	allNodes = [...allNodes, ...nodesClonedInSubtree];
	// merge the newly cloned edges with the current edges
	const allEdges = [...clonedEdges, ...filteredEdges];

	return { nodes: allNodes, edges: allEdges };
};

// -------------------------------------------------------------------
// 6. checkActionExists
// -------------------------------------------------------------------

export const checkActionExists = (
	action: RehabAction,
	elements: Elements,
): boolean => {
	const node = elements.find(node => {
		const reactNode = node as ReactFlowSchema;
		if (reactNode.type == NODE_TYPES.action) {
			if (reactNode.data && reactNode.data.label == action.actionId) {
				return true;
			}
		} else if (reactNode.type == NODE_TYPES.query) {
			const rules = reactNode.data?.nodeData.rules;
			if (rules) {
				const existingActionRule = rules.find(
					r =>
						r.recordType === RECORD_TYPE.ACTION &&
						r.config === action._id,
				);
				return !!existingActionRule;
			}
		}

		return false;
	});

	return !!node;
};

// -------------------------------------------------------------------
// 8. getQueryFormValues
// -------------------------------------------------------------------

export const getQueryFormValues = (
	elements: Elements,
	nodeIdToEdit: string | undefined,
): { name: string; id: string | null; type: string; rules: Rules[] } => {
	const defaultFormValues = {
		name: '',
		id: null as string | null,
		type: NODE_TYPES.query,
		rules: [] as Rules[],
	};
	if (!nodeIdToEdit) {
		return defaultFormValues;
	}

	const nodeToEdit = elements.find(el => el.id === nodeIdToEdit) as
		| Node
		| undefined;

	return {
		name: nodeToEdit?.data.label,
		id: nodeToEdit?.id ?? null,
		type: NODE_TYPES.query,
		rules: nodeToEdit?.data.nodeData.rules ?? [],
	};
};
